import { useMemo } from "react";
import { Clock, FileText, Loader2, Sparkles, Video } from "lucide-react";
import { cn } from "../../../../lib/utils";
import { CompanyLogo, getDomainFromCompany } from "../../../../components/ui/CompanyLogo";
import { GroupBySelector, type GroupByField } from "../../../../components/ui/GroupBySelector";
import { KanbanBoard } from "../../../../components/ui/KanbanBoard";
import {
  normalizeMeetingDocStatus,
} from "../../../../services/meetingDocsHelpers";
import type { MeetingDoc } from "../../../../services/meetingDocsEndpoint";
import type { MeetingBoardGroupBy, MeetingRowData } from "../meetingsTypes";
import { MEETING_GROUP_ORDER } from "../meetingsTypes";

type MeetingsBoardProps = {
  meetings: MeetingRowData[];
  groupBy: MeetingBoardGroupBy;
  onGroupByChange: (value: MeetingBoardGroupBy) => void;
  onMeetingAction?: (meeting: MeetingRowData) => void;
  onDocOpen?: (meeting: MeetingRowData & { docOverride?: MeetingDoc }) => void;
  onMeetingClick?: (meeting: MeetingRowData) => void;
  preparingMeetingIds?: ReadonlySet<string>;
  followingUpMeetingIds?: ReadonlySet<string>;
};

const groupByFields: GroupByField[] = [
  { key: "time_group", label: "Time Period", values: [...MEETING_GROUP_ORDER] },
  { key: "action", label: "Action Type", values: ["Prep", "Follow up", "Followed up", "No action"] },
  { key: "company", label: "Company", values: ["With company", "Internal"] },
];

const columnColors: Record<string, string> = {
  "Coming up": "hsl(258, 73%, 65%)",
  Today: "hsl(142, 71%, 45%)",
  Yesterday: "hsl(212, 10%, 56%)",
  Earlier: "hsl(0, 0%, 72%)",
};

const actionStyles = {
  prep: "border-primary/20 bg-primary/10 text-primary",
  follow_up: "border-status-in-progress/20 bg-status-in-progress/10 text-status-in-progress",
  followed_up: "border-border bg-muted text-muted-foreground",
} as const;

const getBoardColumnValue = (meeting: MeetingRowData, field: MeetingBoardGroupBy): string => {
  if (field === "time_group") return meeting.group;
  if (field === "action") {
    if (!meeting.action) return "No action";
    if (meeting.action.type === "prep") return "Prep";
    if (meeting.action.type === "follow_up") return "Follow up";
    return "Followed up";
  }
  return meeting.company ? "With company" : "Internal";
};

const DocChips = ({
  meeting,
  onDocOpen,
}: {
  meeting: MeetingRowData;
  onDocOpen?: (meeting: MeetingRowData & { docOverride?: MeetingDoc }) => void;
}) => {
  const prepReady = meeting.prepDoc && normalizeMeetingDocStatus(meeting.prepDoc.status) === "success";
  const followupReady = meeting.followupDoc && normalizeMeetingDocStatus(meeting.followupDoc.status) === "success";

  if (!prepReady && !followupReady) return null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {prepReady && (
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/20 transition-colors"
          onClick={(e) => { e.stopPropagation(); onDocOpen?.({ ...meeting, docOverride: meeting.prepDoc }); }}
        >
          <Sparkles className="h-2.5 w-2.5" /> Prep Brief
        </button>
      )}
      {followupReady && (
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/20 transition-colors"
          onClick={(e) => { e.stopPropagation(); onDocOpen?.({ ...meeting, docOverride: meeting.followupDoc }); }}
        >
          <Sparkles className="h-2.5 w-2.5" /> Follow-up Brief
        </button>
      )}
    </div>
  );
};

const MeetingCard = ({
  meeting,
  onMeetingAction,
  onDocOpen,
  onMeetingClick,
  preparingMeetingIds,
  followingUpMeetingIds,
}: {
  meeting: MeetingRowData;
  onMeetingAction?: (meeting: MeetingRowData) => void;
  onDocOpen?: (meeting: MeetingRowData & { docOverride?: MeetingDoc }) => void;
  onMeetingClick?: (meeting: MeetingRowData) => void;
  preparingMeetingIds?: ReadonlySet<string>;
  followingUpMeetingIds?: ReadonlySet<string>;
}) => {
  const companyDomain = meeting.company?.domain || (meeting.company?.name ? getDomainFromCompany(meeting.company.name) : "");
  const isPrep = meeting.action?.type === "prep";
  const isFollowUp = meeting.action?.type === "follow_up";
  const isPrepFailed = meeting.prepStatus === "failed";
  const isFollowupFailed = meeting.followupStatus === "failed";

  const canTriggerAction = (isPrep || isFollowUp) && typeof onMeetingAction === "function";
  const prepIsInProgress = Boolean(
    meeting.prepStatus === "pending"
    || meeting.prepStatus === "running"
    || preparingMeetingIds?.has(meeting.id),
  );
  const followupIsInProgress = Boolean(
    meeting.followupStatus === "pending"
    || meeting.followupStatus === "running"
    || followingUpMeetingIds?.has(meeting.id),
  );
  const isLoading = (isPrep && prepIsInProgress) || (isFollowUp && followupIsInProgress);

  const loadingLabel = isFollowUp ? "Following up..." : "Preparing...";
  const failedLabel = isFollowUp ? "Retry follow-up" : "Retry prep";
  const isFailed = isFollowUp ? isFollowupFailed : isPrepFailed;

  return (
    <div
      className="rounded-lg border border-border-strong bg-card p-3.5 transition-colors hover:border-border-chip cursor-pointer"
      onClick={() => onMeetingClick?.(meeting)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onMeetingClick?.(meeting); }}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex-1 truncate text-sm font-medium text-foreground">{meeting.title}</span>
        <div className="flex flex-shrink-0 items-center gap-1">
          {meeting.hasRecording && <Video className="h-3.5 w-3.5 text-muted-foreground" />}
          {meeting.hasNotes && <FileText className="h-3.5 w-3.5 text-muted-foreground" />}
        </div>
      </div>

      <div className="mt-1 flex items-center gap-1.5">
        <Clock className="h-3 w-3 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{meeting.timeLabel}</span>
      </div>

      {meeting.company && (
        <div className="mt-2.5 flex items-center gap-2">
          <CompanyLogo
            domain={companyDomain}
            name={meeting.company.name}
            className="h-5 w-5 rounded"
          />
          <span className="truncate text-xs text-muted-foreground">{meeting.company.name}</span>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        {isLoading ? (
          <span className={cn("inline-flex items-center gap-1 rounded-[5px] border px-2 py-0.5 text-[11px] font-medium", actionStyles.followed_up)}>
            <Loader2 className="h-3 w-3 animate-spin" />
            {loadingLabel}
          </span>
        ) : meeting.action && canTriggerAction ? (
          <button
            type="button"
            className={cn("inline-flex items-center rounded-[5px] border px-2 py-0.5 text-[11px] font-medium", actionStyles[meeting.action.type])}
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onMeetingAction!(meeting); }}
            disabled={isLoading}
            aria-label={isPrep ? `Prep for meeting ${meeting.title}` : `Follow up on meeting ${meeting.title}`}
          >
            {isFailed ? failedLabel : meeting.action.label}
          </button>
        ) : meeting.action ? (
          <span className={cn("inline-flex items-center rounded-[5px] border px-2 py-0.5 text-[11px] font-medium", actionStyles[meeting.action.type])}>
            {meeting.action.label}
          </span>
        ) : (
          <span />
        )}

        {meeting.attendees.length > 0 && (
          <div className="flex items-center -space-x-1.5">
            {meeting.attendees.slice(0, 3).map((attendee) => (
              <div
                key={`${meeting.id}-${attendee.name}`}
                className="flex h-5 w-5 items-center justify-center rounded-full bg-muted-foreground text-[9px] text-white ring-2 ring-card"
              >
                {attendee.initials}
              </div>
            ))}
            {meeting.attendees.length > 3 && (
              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[9px] text-muted-foreground ring-2 ring-card">
                +{meeting.attendees.length - 3}
              </div>
            )}
          </div>
        )}
      </div>

      <DocChips meeting={meeting} onDocOpen={onDocOpen} />
    </div>
  );
};

const MeetingsBoard = ({
  meetings,
  groupBy,
  onGroupByChange,
  onMeetingAction,
  onDocOpen,
  onMeetingClick,
  preparingMeetingIds,
  followingUpMeetingIds,
}: MeetingsBoardProps) => {
  const columns = useMemo(
    () => groupByFields.find((field) => field.key === groupBy)?.values || [],
    [groupBy],
  );

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center gap-2">
        <GroupBySelector
          fields={groupByFields}
          selectedField={groupBy}
          onFieldChange={(nextValue) => onGroupByChange(nextValue as MeetingBoardGroupBy)}
        />
      </div>

      <KanbanBoard<MeetingRowData>
        items={meetings}
        groupByField={groupBy}
        getGroupValue={(meeting) => getBoardColumnValue(meeting, groupBy)}
        columns={columns}
        columnColors={groupBy === "time_group" ? columnColors : undefined}
        renderCard={(meeting) => (
          <MeetingCard
            meeting={meeting}
            onMeetingAction={onMeetingAction}
            onDocOpen={onDocOpen}
            onMeetingClick={onMeetingClick}
            preparingMeetingIds={preparingMeetingIds}
            followingUpMeetingIds={followingUpMeetingIds}
          />
        )}
        onDragEnd={() => {}}
        showEmptyColumns
      />
    </div>
  );
};

export default MeetingsBoard;
